import {type ChangeEvent, type MouseEvent, useEffect, useMemo, useState} from 'react';
import {type SelectChangeEvent, useMediaQuery, useTheme} from '@mui/material';
import {useTranslation} from 'react-i18next';
import type {Contract, ContractListItem} from '../types/contract';
import type {Employee} from '../types/employee';
import {useAuth} from '../context/AuthContext';
import {API_BASE_URL} from '../config/apiConfig';
import i18n from '../i18n';
import {type ContractValidationErrors, isContractFormValid, validateContractForm} from '../components/Generic/NewContractFormComponent';
import {
    type ContractPreviewResult,
    downloadContractDocument,
    getContractsByBranch,
    previewContract as previewContractApi,
    signContract as signContractApi,
    updateContract,
} from '../services/contractService';
import {addContract, getEmployees, terminateContract} from '../services/employeeService';

const ALL_BRANCHES = 'All';
const DEFAULT_UPLOAD_WORKLOAD = '100';

export interface ContractUploadRowError {
    row?: number;
    column?: string;
    message: string;
}

export interface ContractUploadError {
    message: string;
    errors: ContractUploadRowError[];
}

interface JobPositionOption {
    id: string;
    name: string;
    branch: string;
    isActive: boolean;
    status: string;
}

interface ApprovedBranch {
    id: string;
    name: string;
}

export interface SalaryBasisOption {
    id: string;
    name: string;
}

export interface CalcTypeOption {
    id: string;
    name: string;
}

export interface JobPositionUploadOption {
    id: string;
    name: string;
}

// The native `<input type="date">` calendar only mirrors a value that is
// already in exact "YYYY-MM-DD" form — anything else (ISO datetime, "DD/MM/YYYY",
// "DD.MM.YYYY", ...) is silently ignored by the browser, leaving the picker
// blank even though the field still holds the raw text.
export function toDateInputValue(value: string | null | undefined): string {
    if (!value) return '';
    const trimmed = String(value).trim();
    if (!trimmed) return '';
    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;

    const isoMatch = trimmed.match(/^(\d{4}-\d{2}-\d{2})T/);
    if (isoMatch) return isoMatch[1];

    const dmy = trimmed.match(/^(\d{1,2})[./-](\d{1,2})[./-](\d{4})$/);
    if (dmy) {
        let day = parseInt(dmy[1], 10);
        let month = parseInt(dmy[2], 10);
        const y = dmy[3];
        // Default to D/M/Y, but if the second number can't be a month (>12) while the
        // first one can be, the source is actually M/D/Y (e.g. "8/18/2026") — swap.
        if (month > 12 && day <= 12) {
            [day, month] = [month, day];
        }
        if (month > 12 || day > 31) return '';
        return `${y}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    }

    const parsed = new Date(trimmed);
    if (!isNaN(parsed.getTime())) {
        const y = parsed.getFullYear();
        const m = String(parsed.getMonth() + 1).padStart(2, '0');
        const d = String(parsed.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }

    return '';
}

const isActiveContractStatus = (status: string): boolean => (status || '').toLowerCase() === 'active';

// A contract is "ending soon" when it's active and its end date falls within the next 30 days.
export function isContractEndingSoon(contract: Pick<ContractListItem, 'status' | 'endDate'>): boolean {
    if (!isActiveContractStatus(contract.status)) return false;
    const endIso = toDateInputValue(contract.endDate);
    if (!endIso) return false;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const end = new Date(endIso);
    const diffDays = Math.round((end.getTime() - today.getTime()) / 86400000);
    return diffDays >= 0 && diffDays <= 30;
}

export function useContracts(
    newContract: Contract,
    branchName: string | undefined,
    handleContractFormChange: (field: keyof Contract) => (event: any) => void,
    showAllErrors: boolean = false
) {
    const {token} = useAuth();

    const [touched, setTouched] = useState<Partial<Record<keyof Contract, boolean>>>({});
    const [jobPositions, setJobPositions] = useState<JobPositionOption[]>([]);
    const [jobPositionsLoading, setJobPositionsLoading] = useState<boolean>(false);
    const [approvedBranchJobPositions, setApprovedBranchJobPositions] = useState<JobPositionOption[]>([]);
    const [approvedBranchJobPositionsLoading, setApprovedBranchJobPositionsLoading] = useState<boolean>(false);
    const [salaryBasisOptions, setSalaryBasisOptions] = useState<SalaryBasisOption[]>([]);
    const [salaryBasisLoading, setSalaryBasisLoading] = useState<boolean>(false);
    const [calcTypeOptions, setCalcTypeOptions] = useState<CalcTypeOption[]>([]);
    const [calcTypeLoading, setCalcTypeLoading] = useState<boolean>(false);

    useEffect(() => {
        if (!token || !branchName) {
            setJobPositions([]);
            return;
        }
        let cancelled = false;
        setJobPositionsLoading(true);
        fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to fetch branches: ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                if (cancelled) return null;
                const branches: ApprovedBranch[] = data.tenants ?? [];
                const branchId = branches.find((b) => b.name === branchName)?.id ?? branchName;
                return fetch(`${API_BASE_URL}/api/job-positions?branchId=${encodeURIComponent(branchId)}`, {
                    method: 'GET',
                    headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
                });
            })
            .then((response) => {
                if (!response) return null;
                if (!response.ok) throw new Error(`Failed to fetch job positions: ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                if (!cancelled && data) setJobPositions(data.jobs ?? []);
            })
            .catch(() => {
                if (!cancelled) setJobPositions([]);
            })
            .finally(() => {
                if (!cancelled) setJobPositionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token, branchName]);

    useEffect(() => {
        if (!token || !branchName) {
            setApprovedBranchJobPositions([]);
            return;
        }
        let cancelled = false;
        setApprovedBranchJobPositionsLoading(true);
        fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to fetch branches: ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                if (cancelled) return null;
                const branches: ApprovedBranch[] = data.tenants ?? [];
                const branchId = branches.find((b) => b.name === branchName)?.id ?? branchName;
                return fetch(`${API_BASE_URL}/api/job-positions/status/approved/branches?branchId=${encodeURIComponent(branchId)}`, {
                    method: 'GET',
                    headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
                });
            })
            .then((response) => {
                if (!response) return null;
                if (!response.ok) throw new Error(`Failed to fetch approved branch job positions: ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                if (!cancelled && data) setApprovedBranchJobPositions(data.jobs ?? data ?? []);
            })
            .catch(() => {
                if (!cancelled) setApprovedBranchJobPositions([]);
            })
            .finally(() => {
                if (!cancelled) setApprovedBranchJobPositionsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token, branchName]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setSalaryBasisLoading(true);
        fetch(`${API_BASE_URL}/api/contract/salaryBasis/get`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to fetch salary basis options: ${response.statusText}`);
                return response.json();
            })
            .then((data: SalaryBasisOption[]) => {
                if (!cancelled) setSalaryBasisOptions(data ?? []);
            })
            .catch(() => {
                if (!cancelled) setSalaryBasisOptions([]);
            })
            .finally(() => {
                if (!cancelled) setSalaryBasisLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setCalcTypeLoading(true);
        fetch(`${API_BASE_URL}/api/contract/calcTypes/get`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to fetch calc types: ${response.statusText}`);
                return response.json();
            })
            .then((data: CalcTypeOption[]) => {
                if (!cancelled) setCalcTypeOptions(data ?? []);
            })
            .catch(() => {
                if (!cancelled) setCalcTypeOptions([]);
            })
            .finally(() => {
                if (!cancelled) setCalcTypeLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    // The contract list/GET responses carry the NAME of the payroll calc type and salary basis, while
    // these selects use the option ID (also what create/edit send). Once the options are loaded,
    // translate a name held in the form into the matching option id so the select shows it.
    useEffect(() => {
        if (calcTypeOptions.length === 0) return;
        const current = newContract.payrollCalcType;
        if (!current || calcTypeOptions.some((o) => o.id === current)) return;
        const match = calcTypeOptions.find((o) => o.name.trim().toLowerCase() === current.trim().toLowerCase());
        if (match) handleContractFormChange('payrollCalcType')({target: {value: match.id}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [calcTypeOptions, newContract.payrollCalcType]);

    useEffect(() => {
        if (salaryBasisOptions.length === 0) return;
        const current = newContract.salaryBasis;
        if (!current || salaryBasisOptions.some((o) => o.id === current)) return;
        const match = salaryBasisOptions.find((o) => o.name.trim().toLowerCase() === current.trim().toLowerCase());
        if (match) handleContractFormChange('salaryBasis')({target: {value: match.id}});
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [salaryBasisOptions, newContract.salaryBasis]);

    const handleBlur = (field: keyof Contract) => () => {
        setTouched(prev => ({...prev, [field]: true}));
    };

    const isTouched = (field: keyof Contract) => touched[field] || showAllErrors;

    const errors: ContractValidationErrors = validateContractForm(newContract);

    const handleSwitchChange = (field: keyof Contract) => (event: ChangeEvent<HTMLInputElement>) => {
        handleContractFormChange(field)({
            target: {
                value: event.target.checked ? '1' : '0'
            }
        });
    };

    return {
        touched,
        jobPositions,
        jobPositionsLoading,
        approvedBranchJobPositions,
        approvedBranchJobPositionsLoading,
        salaryBasisOptions,
        salaryBasisLoading,
        calcTypeOptions,
        calcTypeLoading,
        handleBlur,
        isTouched,
        errors,
        handleSwitchChange,
    };
}

export interface UploadedContract {
    _rowId: number;
    employeePersonalNo: string;
    resolvedEmployeeId: string;
    contractNumber?: string;
    startDate: string;
    endDate: string;
    status: string;
    jobPosition: string;
    payrollCalcType: string;
    salaryBasis: string;
    baseAmount: string;
    employerInsuranceBenefit: string;
    familyInsuranceDeduction: string;
    withContribution: string;
    workload: string;
    fundEnabled: string;
    notes: string;
}

export interface UploadedContractErrors {
    employeePersonalNo?: string;
    contractNumber?: string;
    startDate?: string;
    endDate?: string;
    jobPosition?: string;
    payrollCalcType?: string;
    withContribution?: string;
    salaryBasis?: string;
    baseAmount?: string;
    workload?: string;
    employerInsuranceBenefit?: string;
    familyInsuranceDeduction?: string;
}

export function validateUploadedContract(contract: UploadedContract): UploadedContractErrors {
    const errors: UploadedContractErrors = {};
    if (!contract.resolvedEmployeeId) errors.employeePersonalNo = i18n.t('employee.contract.upload.noMatchingEmployee');
    if (!(contract.startDate || '').trim()) errors.startDate = i18n.t('employee.contract.validation.startDateRequired');
    if ((contract.endDate || '').trim() && (contract.startDate || '').trim()) {
        if (new Date(contract.endDate) <= new Date(contract.startDate)) {
            errors.endDate = i18n.t('employee.contract.validation.endDateAfterStart');
        }
    }
    if (!(contract.jobPosition || '').trim()) errors.jobPosition = i18n.t('employee.contract.validation.jobPositionRequired');
    if (!(contract.payrollCalcType || '').trim()) errors.payrollCalcType = i18n.t('employee.contract.validation.payrollCalcTypeRequired');
    if (!(contract.withContribution || '').trim()) errors.withContribution = i18n.t('employee.contract.validation.withContributionRequired');
    if (!(contract.salaryBasis || '').trim()) errors.salaryBasis = i18n.t('employee.contract.validation.salaryBasisRequired');
    if (!(contract.baseAmount || '').trim()) errors.baseAmount = i18n.t('employee.contract.validation.baseAmountRequired');
    if (!(contract.workload || '').trim()) errors.workload = i18n.t('employee.contract.validation.workloadRequired');
    if (!(contract.employerInsuranceBenefit || '').trim()) errors.employerInsuranceBenefit = i18n.t('employee.contract.validation.employerInsuranceBenefitRequired');
    if (!(contract.familyInsuranceDeduction || '').trim()) errors.familyInsuranceDeduction = i18n.t('employee.contract.validation.familyInsuranceDeductionRequired');
    return errors;
}

// ─── Upload Preview Dialog: field config ────────────────────────────────────

export type ContractFieldType =
    | 'employee'
    | 'text'
    | 'number'
    | 'date'
    | 'calcType'
    | 'withContribution'
    | 'salaryBasis'
    | 'jobPosition';

export interface ContractFieldDef {
    labelKey: string;
    field: keyof UploadedContract;
    type: ContractFieldType;
}

export const CONTRACT_FIELDS: ContractFieldDef[] = [
    {labelKey: 'employee.contract.field.employee', field: 'resolvedEmployeeId', type: 'employee'},
    {labelKey: 'employee.contract.field.startDate', field: 'startDate', type: 'date'},
    {labelKey: 'employee.contract.field.endDate', field: 'endDate', type: 'date'},
    {labelKey: 'employee.contract.field.jobPosition', field: 'jobPosition', type: 'jobPosition'},
    {labelKey: 'employee.contract.field.payrollCalcType', field: 'payrollCalcType', type: 'calcType'},
    {labelKey: 'employee.contract.field.withContribution', field: 'withContribution', type: 'withContribution'},
    {labelKey: 'employee.contract.field.salaryBasis', field: 'salaryBasis', type: 'salaryBasis'},
    {labelKey: 'employee.contract.field.baseAmount', field: 'baseAmount', type: 'number'},
    {labelKey: 'employee.contract.field.workload', field: 'workload', type: 'number'},
    {labelKey: 'employee.contract.field.employerInsuranceBenefit', field: 'employerInsuranceBenefit', type: 'number'},
    {labelKey: 'employee.contract.field.familyInsuranceDeduction', field: 'familyInsuranceDeduction', type: 'number'},
];

// ─── Upload Preview Dialog: option validity helpers ─────────────────────────

export function isCalcTypeValid(value: string, calcTypeOptions: CalcTypeOption[]): boolean {
    return calcTypeOptions.length === 0 || calcTypeOptions.some((o) => o.id === value);
}

export function isSalaryBasisValid(value: string, salaryBasisOptions: SalaryBasisOption[]): boolean {
    return salaryBasisOptions.length === 0 || salaryBasisOptions.some((o) => o.id === value);
}

export function isJobPositionValid(value: string, jobPositionOptions: JobPositionUploadOption[], loading: boolean = false): boolean {
    if (loading) return true; // still fetching — don't mark invalid yet
    return jobPositionOptions.some((o) => o.id === value);
}

// ─── Upload Preview Dialog: state/behaviour hook ─────────────────────────────

interface UseUploadContractPreviewDialogParams {
    open: boolean;
    contracts: UploadedContract[];
    employees: Employee[];
    calcTypeOptions: CalcTypeOption[];
    salaryBasisOptions: SalaryBasisOption[];
    onChange: (rowId: number, field: keyof UploadedContract, value: any) => void;
    onSave: () => void;
}

export function useUploadContractPreviewDialog({
    open,
    contracts,
    employees,
    calcTypeOptions,
    salaryBasisOptions,
    onChange,
    onSave,
}: UseUploadContractPreviewDialogParams) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
    const {token} = useAuth();

    const [page, setPage] = useState(1);
    const [search, setSearch] = useState('');
    const [showAllErrors, setShowAllErrors] = useState(false);
    const [jobPositionOptions, setJobPositionOptions] = useState<JobPositionUploadOption[]>([]);
    const [jobPositionLoading, setJobPositionLoading] = useState(false);
    const [jobPositionLoaded, setJobPositionLoaded] = useState(false);

    // Auto-resolve payrollCalcType and salaryBasis from plain names → ids
    // (upload response returns plain text values; selects need the id)
    useEffect(() => {
        if (!open) return;
        if (calcTypeOptions.length === 0 && salaryBasisOptions.length === 0) return;
        contracts.forEach((c) => {
            if (calcTypeOptions.length > 0) {
                const alreadyId = calcTypeOptions.some((o) => o.id === c.payrollCalcType);
                if (!alreadyId && c.payrollCalcType) {
                    const match = calcTypeOptions.find(
                        (o) => o.name.toLowerCase() === c.payrollCalcType.toLowerCase()
                    );
                    if (match) onChange(c._rowId, 'payrollCalcType', match.id);
                }
            }
            if (salaryBasisOptions.length > 0) {
                const alreadyId = salaryBasisOptions.some((o) => o.id === c.salaryBasis);
                if (!alreadyId && c.salaryBasis) {
                    const match = salaryBasisOptions.find(
                        (o) => o.name.toLowerCase() === c.salaryBasis.toLowerCase()
                    );
                    if (match) onChange(c._rowId, 'salaryBasis', match.id);
                }
            }
        });
    }, [open, calcTypeOptions.length, salaryBasisOptions.length]);

    useEffect(() => {
        if (open) {
            setPage(1);
            setSearch('');
            setShowAllErrors(false);
        }
    }, [open, contracts.length]);

    const filtered = useMemo(() => {
        const q = search.trim().toLowerCase();
        if (!q) return contracts;
        const emp = (c: UploadedContract) => {
            const e = employees.find((em) => String(em.id) === c.resolvedEmployeeId);
            return e ? `${e.firstName} ${e.lastName} ${e.personalNo}` : c.employeePersonalNo;
        };
        const job = (c: UploadedContract) =>
            jobPositionOptions.find((o) => o.id === c.jobPosition)?.name ?? c.jobPosition;
        return contracts.filter((c) =>
            [emp(c), c.contractNumber, c.startDate, job(c)]
                .some((v) => (v ?? '').toLowerCase().includes(q))
        );
    }, [contracts, search, employees]);

    useEffect(() => {
        setPage(1);
    }, [filtered.length]);

    const totalPages = filtered.length;
    const contract = filtered[page - 1];

    const currentBranchId = useMemo(
        () => employees.find((e) => String(e.id) === contract?.resolvedEmployeeId)?.branchId,
        [employees, contract],
    );

    // Fetch job positions for the current contract's branch on every page navigation.
    useEffect(() => {
        if (!open || !token || !currentBranchId) {
            setJobPositionOptions([]);
            return;
        }
        let cancelled = false;
        setJobPositionLoaded(false);
        setJobPositionLoading(true);
        const fetchAllPages = async (): Promise<JobPositionUploadOption[]> => {
            const all: JobPositionUploadOption[] = [];
            let pageIndex = 0;
            let totalPages = 1;
            do {
                const url = `${API_BASE_URL}/api/job-positions/status/approved/branches?branchId=${encodeURIComponent(currentBranchId)}&page=${pageIndex}`;
                const r = await fetch(url, {
                    method: 'GET',
                    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                });
                if (!r.ok) break;
                const data = await r.json();
                const jobs: JobPositionUploadOption[] = data.jobs ?? [];
                all.push(...jobs);
                totalPages = data.totalPages ?? 1;
                pageIndex += 1;
            } while (pageIndex < totalPages && !cancelled);
            return all;
        };

        fetchAllPages()
            .then((jobs) => {
                if (cancelled) return;
                setJobPositionOptions(jobs);
            })
            .catch(() => {
                if (!cancelled) setJobPositionOptions([]);
            })
            .finally(() => {
                if (!cancelled) { setJobPositionLoading(false); setJobPositionLoaded(true); }
            });
        return () => {
            cancelled = true;
        };
    }, [open, token, contract?._rowId]);

    // Auto-resolve the current contract's jobPosition from a plain name → id
    // once its branch's options have loaded (upload response returns plain text).
    useEffect(() => {
        if (!open || !contract || jobPositionOptions.length === 0) return;
        const alreadyId = jobPositionOptions.some((o) => o.id === contract.jobPosition);
        if (!alreadyId && contract.jobPosition) {
            const match = jobPositionOptions.find(
                (o) => o.name.toLowerCase() === contract.jobPosition.toLowerCase()
            );
            if (match) onChange(contract._rowId, 'jobPosition', match.id);
        }
    }, [open, contract, jobPositionOptions]);

    const currentErrors: UploadedContractErrors = useMemo(
        () => (contract && showAllErrors ? validateUploadedContract(contract) : {}),
        [contract, showAllErrors],
    );

    const invalidCount = useMemo(
        () =>
            showAllErrors
                ? contracts.filter((c) => Object.keys(validateUploadedContract(c)).length > 0).length
                : 0,
        [contracts, showAllErrors],
    );

    const handleSaveClick = () => {
        const firstInvalid = contracts.find(
            (c) => Object.keys(validateUploadedContract(c)).length > 0,
        );
        if (firstInvalid) {
            setShowAllErrors(true);
            setSearch('');
            const idx = contracts.findIndex((c) => c._rowId === firstInvalid._rowId);
            if (idx >= 0) setPage(idx + 1);
            return;
        }
        onSave();
    };

    return {
        isMobile,
        fullScreen,
        page,
        setPage,
        search,
        setSearch,
        showAllErrors,
        totalPages,
        contract,
        currentErrors,
        invalidCount,
        handleSaveClick,
        jobPositionOptions,
        jobPositionLoading,
        jobPositionLoaded,
    };
}

export function useContractUpload(
    employees: import('../types/employee').Employee[],
    onSaveSuccess?: () => void,
) {
    const { token } = useAuth();
    const { t } = useTranslation();

    const [uploadingContracts, setUploadingContracts] = useState<boolean>(false);
    const [contractUploadPreviewOpen, setContractUploadPreviewOpen] = useState<boolean>(false);
    const [uploadedContracts, setUploadedContracts] = useState<UploadedContract[]>([]);
    const [contractUploadSaveLoading, setContractUploadSaveLoading] = useState<boolean>(false);
    const [contractUploadSaveError, setContractUploadSaveError] = useState<{ message: string; violations: string[] } | null>(null);
    const [contractSalaryBasisOptions, setContractSalaryBasisOptions] = useState<SalaryBasisOption[]>([]);
    const [contractSalaryBasisLoading, setContractSalaryBasisLoading] = useState<boolean>(false);
    const [contractCalcTypeOptions, setContractCalcTypeOptions] = useState<CalcTypeOption[]>([]);
    const [contractCalcTypeLoading, setContractCalcTypeLoading] = useState<boolean>(false);
    const [contractUploadError, setContractUploadError] = useState<ContractUploadError | null>(null);
    const [contractTemplateDownloading, setContractTemplateDownloading] = useState<boolean>(false);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setContractSalaryBasisLoading(true);
        fetch(`${API_BASE_URL}/api/contract/salaryBasis/get`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        })
            .then(r => {
                if (!r.ok) throw new Error();
                return r.json();
            })
            .then((data: SalaryBasisOption[]) => {
                if (!cancelled) setContractSalaryBasisOptions(data ?? []);
            })
            .catch(() => {
                if (!cancelled) setContractSalaryBasisOptions([]);
            })
            .finally(() => {
                if (!cancelled) setContractSalaryBasisLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setContractCalcTypeLoading(true);
        fetch(`${API_BASE_URL}/api/contract/calcTypes/get`, {
            method: 'GET',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        })
            .then(r => {
                if (!r.ok) throw new Error();
                return r.json();
            })
            .then((data: CalcTypeOption[]) => {
                if (!cancelled) setContractCalcTypeOptions(data ?? []);
            })
            .catch(() => {
                if (!cancelled) setContractCalcTypeOptions([]);
            })
            .finally(() => {
                if (!cancelled) setContractCalcTypeLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const downloadContractTemplate = async (): Promise<void> => {
        if (!token) return;
        setContractTemplateDownloading(true);
        try {
            const response = await fetch(`${API_BASE_URL}/api/contract/template`, {
                method: 'GET',
                headers: { Authorization: `Bearer ${token}` },
            });
            if (!response.ok) throw new Error('Template download failed');
            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'upload_contract_template.xlsx';
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Failed to download contract template:', err);
            setContractUploadError({message: t('employee.upload.error.templateFailed'), errors: []});
        } finally {
            setContractTemplateDownloading(false);
        }
    };

    const handleUploadContracts = async (file: File): Promise<void> => {
        if (!token) return;
        setUploadingContracts(true);
        try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await fetch(`${API_BASE_URL}/api/contract/upload`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: formData,
            });
            if (!response.ok) {
                let body: any = {};
                try { body = await response.json(); } catch { /* non-JSON error body */ }
                setContractUploadError({
                    message: body.message || t('employee.upload.error.generic'),
                    errors: Array.isArray(body.errors) ? body.errors : [],
                });
                return;
            }
            setContractUploadError(null);
            const raw = await response.json();
            // Normalise: bare array OR any common wrapper key
            const list: any[] = Array.isArray(raw)
                ? raw
                : (raw?.contracts ?? raw?.data ?? raw?.items ?? raw?.list ?? raw?.payload
                    ?? (raw && typeof raw === 'object'
                        ? (Object.values(raw).find((v) => Array.isArray(v)) as any[] | undefined) ?? []
                        : []));
            const mapped: UploadedContract[] = list.map((item, idx) => {
                const emp = employees.find(e => e.personalNo === (item.employeePersonalNo ?? ''))
                    ?? (item.employeeId ? employees.find(e => String(e.id) === String(item.employeeId)) : undefined);
                return {
                    _rowId: idx,
                    employeePersonalNo: item.employeePersonalNo ?? '',
                    resolvedEmployeeId: emp ? String(emp.id) : '',
                    contractNumber: item.contractNumber ?? '',
                    startDate: toDateInputValue(item.startDate),
                    endDate: toDateInputValue(item.endDate),
                    status: item.status ?? 'active',
                    jobPosition: item.jobPosition ?? '',
                    payrollCalcType: item.payrollCalcType ?? '',
                    salaryBasis: item.salaryBasis ?? '',
                    baseAmount: item.baseAmount ?? '',
                    employerInsuranceBenefit: item.employerInsuranceBenefit ?? '',
                    familyInsuranceDeduction: item.familyInsuranceDeduction ?? '',
                    withContribution: '1',
                    workload: DEFAULT_UPLOAD_WORKLOAD,
                    fundEnabled: '0',
                    notes: '',
                };
            });
            setUploadedContracts(mapped);
            setContractUploadPreviewOpen(true);
        } catch (err) {
            console.error('Failed to upload contracts file:', err);
            setContractUploadError({message: t('employee.upload.error.generic'), errors: []});
        } finally {
            setUploadingContracts(false);
        }
    };

    const handleUploadedContractChange = (rowId: number, field: keyof UploadedContract, value: any): void => {
        setUploadedContracts(prev =>
            prev.map(c => {
                if (c._rowId !== rowId) return c;
                if (field === 'resolvedEmployeeId') {
                    const emp = employees.find(e => String(e.id) === value);
                    return { ...c, resolvedEmployeeId: value, employeePersonalNo: emp ? emp.personalNo : c.employeePersonalNo };
                }
                return { ...c, [field]: value };
            })
        );
    };

    const handleCloseContractUploadPreview = (): void => {
        setContractUploadPreviewOpen(false);
        setUploadedContracts([]);
        setContractUploadSaveError(null);
    };

    const handleSaveUploadedContracts = async (): Promise<void> => {
        if (!token) {
            setContractUploadSaveError({ message: t('employee.upload.error.noAuthToken'), violations: [] });
            return;
        }

        const payload = uploadedContracts.map((c) => ({
            employeeId: c.resolvedEmployeeId,
            startDate: c.startDate,
            endDate: c.endDate || null,
            jobPosition: c.jobPosition,
            payrollCalcType: c.payrollCalcType,
            salaryBasis: c.salaryBasis,
            baseAmount: c.baseAmount,
            workload: (c.workload || '').trim() || DEFAULT_UPLOAD_WORKLOAD, // backend does new BigDecimal(workload)
            employerInsuranceBenefit: c.employerInsuranceBenefit,
            familyInsuranceDeduction: c.familyInsuranceDeduction,
            withContribution: c.withContribution,
            fundEnabled: c.fundEnabled,
            notes: c.notes,
        }));

        setContractUploadSaveLoading(true);
        setContractUploadSaveError(null);

        try {
            const response = await fetch(`${API_BASE_URL}/api/contract/add/list`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });

            if (!response.ok) {
                let errBody: any = {};
                try { errBody = await response.json(); } catch { /* non-JSON body */ }
                setContractUploadSaveError({
                    message: errBody.message || t('employee.upload.error.requestFailed', {status: response.status}),
                    violations: errBody.violations ?? [],
                });
                return;
            }

            handleCloseContractUploadPreview();
            onSaveSuccess?.();
        } catch (err) {
            console.error('Error saving uploaded contracts:', err);
            setContractUploadSaveError({ message: t('common.error.unexpected'), violations: [] });
        } finally {
            setContractUploadSaveLoading(false);
        }
    };

    return {
        uploadingContracts,
        contractUploadPreviewOpen,
        uploadedContracts,
        contractUploadSaveLoading,
        contractUploadSaveError,
        contractSalaryBasisOptions,
        contractSalaryBasisLoading,
        contractCalcTypeOptions,
        contractCalcTypeLoading,
        handleUploadContracts,
        contractUploadError,
        clearContractUploadError: () => setContractUploadError(null),
        downloadContractTemplate,
        contractTemplateDownloading,
        handleUploadedContractChange,
        handleCloseContractUploadPreview,
        handleSaveUploadedContracts,
    };
}

// ─── Contracts module page: branch/contract-number list, edit/view/terminate/pdf ───

const EMPTY_CONTRACT: Contract = {
    contractNumber: '',
    contractId: '',
    startDate: '',
    endDate: '',
    status: 'active',
    jobPosition: '',
    payrollCalcType: '',
    withContribution: '',
    salaryBasis: '',
    baseAmount: '',
    workload: '',
    employerInsuranceBenefit: '',
    familyInsuranceDeduction: '',
    fundEnabled: '',
    notes: '',
};

export function useContractsList() {
    const {token} = useAuth();
    const {t} = useTranslation();

    const rowsPerPage = 10;

    const [branches, setBranches] = useState<ApprovedBranch[]>([]);
    const [branchesLoading, setBranchesLoading] = useState<boolean>(false);
    const [selectedBranch, setSelectedBranch] = useState<string>(ALL_BRANCHES);

    const [contracts, setContracts] = useState<ContractListItem[]>([]);
    const [contractsLoading, setContractsLoading] = useState<boolean>(false);
    const [contractsError, setContractsError] = useState<string>('');

    const [typeFilter, setTypeFilter] = useState<string>('');
    const [searchInput, setSearchInput] = useState<string>('');
    const [page, setPage] = useState<number>(1);

    const [successMessage, setSuccessMessage] = useState<string>('');
    const [errorMessage, setErrorMessage] = useState<string>('');

    // Fetch approved branches for the branch dropdown.
    useEffect(() => {
        if (!token) return;
        let cancelled = false;
        setBranchesLoading(true);
        fetch(`${API_BASE_URL}/api/branches/get?status=APPROVED`, {
            method: 'GET',
            headers: {'Content-Type': 'application/json', Authorization: `Bearer ${token}`},
        })
            .then((response) => {
                if (!response.ok) throw new Error(`Failed to fetch branches: ${response.statusText}`);
                return response.json();
            })
            .then((data) => {
                if (!cancelled) setBranches(data.tenants ?? []);
            })
            .catch(() => {
                if (!cancelled) setBranches([]);
            })
            .finally(() => {
                if (!cancelled) setBranchesLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [token]);

    const refetchContracts = (): Promise<void> => {
        if (!token || !selectedBranch) return Promise.resolve();
        setContractsLoading(true);
        return getContractsByBranch(token, selectedBranch)
            .then((result) => {
                if (result.success) {
                    setContracts(result.contracts ?? []);
                    setContractsError('');
                } else {
                    setContracts([]);
                    setContractsError(result.error || t('contracts.error.fetchFailed'));
                }
            })
            .finally(() => {
                setContractsLoading(false);
            });
    };

    // Fetch contracts whenever the selected branch changes.
    useEffect(() => {
        if (!token || !selectedBranch) {
            setContracts([]);
            setContractsError('');
            return;
        }
        let cancelled = false;
        setContractsLoading(true);
        getContractsByBranch(token, selectedBranch)
            .then((result) => {
                if (cancelled) return;
                if (result.success) {
                    setContracts(result.contracts ?? []);
                    setContractsError('');
                } else {
                    setContracts([]);
                    setContractsError(result.error || t('contracts.error.fetchFailed'));
                }
            })
            .finally(() => {
                if (!cancelled) setContractsLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token, selectedBranch]);

    const handleBranchChange = (event: SelectChangeEvent<string>): void => {
        setSelectedBranch(event.target.value);
        setPage(1);
    };

    const handleTypeFilterChange = (event: SelectChangeEvent<string>): void => {
        setTypeFilter(event.target.value);
        setPage(1);
    };

    const contractStats = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        let activeCount = 0;
        let endingSoonCount = 0;
        let newStartsCount = 0;

        contracts.forEach((c) => {
            if (isActiveContractStatus(c.status)) activeCount += 1;
            if (isContractEndingSoon(c)) endingSoonCount += 1;

            const startIso = toDateInputValue(c.startDate);
            if (startIso) {
                const diffDays = Math.round((today.getTime() - new Date(startIso).getTime()) / 86400000);
                if (diffDays >= 0 && diffDays <= 30) newStartsCount += 1;
            }
        });

        return {activeCount, endingSoonCount, newStartsCount};
    }, [contracts]);

    const filteredContracts = useMemo(() => {
        let list = contracts;
        if (typeFilter === 'open-ended') {
            list = list.filter((c) => !c.endDate);
        } else if (typeFilter === 'fixed-term') {
            list = list.filter((c) => !!c.endDate);
        }
        const q = searchInput.trim().toLowerCase();
        if (q) {
            list = list.filter((c) =>
                [c.contractNumber, c.firstName, c.lastName, c.jobPosition, c.status]
                    .some((v) => (v ?? '').toLowerCase().includes(q))
            );
        }
        return list;
    }, [contracts, typeFilter, searchInput]);

    useEffect(() => {
        setPage(1);
    }, [typeFilter, searchInput, contracts]);

    const paginatedContracts = useMemo(() => {
        const start = (page - 1) * rowsPerPage;
        return filteredContracts.slice(start, start + rowsPerPage);
    }, [filteredContracts, page]);

    const totalPages = Math.max(1, Math.ceil(filteredContracts.length / rowsPerPage));

    // ── View dialog ──
    const [viewDialogOpen, setViewDialogOpen] = useState<boolean>(false);
    const [viewingContract, setViewingContract] = useState<ContractListItem | null>(null);

    const handleView = (contract: ContractListItem): void => {
        setViewingContract(contract);
        setViewDialogOpen(true);
    };

    const handleCloseView = (): void => {
        setViewDialogOpen(false);
        setViewingContract(null);
    };

    // ── Edit dialog ──
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [editingContractId, setEditingContractId] = useState<string | null>(null);
    const [editBranchName, setEditBranchName] = useState<string>('');
    const [editForm, setEditForm] = useState<Contract>(EMPTY_CONTRACT);
    const [editSubmitError, setEditSubmitError] = useState<{ message: string; violations: string[] } | null>(null);
    const [editSubmitting, setEditSubmitting] = useState<boolean>(false);

    const handleEdit = (contract: ContractListItem): void => {
        setEditingContractId(contract.contractId);
        setEditBranchName(contract.tenantName);
        setEditForm({
            contractNumber: contract.contractNumber,
            contractId: contract.contractId,
            startDate: toDateInputValue(contract.startDate),
            endDate: toDateInputValue(contract.endDate),
            status: contract.status,
            jobPosition: contract.jobPosition,
            payrollCalcType: contract.payrollCalcType,
            withContribution: contract.withContribution,
            salaryBasis: contract.salaryBasis,
            baseAmount: contract.baseAmount,
            workload: contract.workload,
            employerInsuranceBenefit: contract.employerInsuranceBenefit,
            familyInsuranceDeduction: contract.familyInsuranceDeduction,
            fundEnabled: contract.fundEnabled,
            notes: contract.notes,
        });
        setEditSubmitError(null);
        setEditDialogOpen(true);
    };

    const handleCloseEditDialog = (): void => {
        setEditDialogOpen(false);
        setEditingContractId(null);
        setEditBranchName('');
        setEditForm(EMPTY_CONTRACT);
        setEditSubmitError(null);
    };

    const handleEditFormChange = (field: keyof Contract) => (event: any): void => {
        setEditForm((prev) => ({...prev, [field]: event.target.value}));
    };

    const handleSubmitEdit = async (): Promise<void> => {
        if (!token || !editingContractId) return;
        if (!isContractFormValid(editForm)) {
            setEditSubmitError({message: t('contracts.edit.validationError'), violations: []});
            return;
        }
        setEditSubmitting(true);
        try {
            const result = await updateContract(token, editingContractId, editForm);
            if (result.success) {
                handleCloseEditDialog();
                await refetchContracts();
                setSuccessMessage(t('contracts.edit.success'));
            } else {
                setEditSubmitError({
                    message: result.error || t('contracts.edit.error'),
                    violations: result.violations || [],
                });
            }
        } catch (err) {
            console.error('Error updating contract:', err);
            setEditSubmitError({message: t('common.error.unexpected'), violations: []});
        } finally {
            setEditSubmitting(false);
        }
    };

    // ── Terminate confirm dialog ──
    const [terminateDialogOpen, setTerminateDialogOpen] = useState<boolean>(false);
    const [terminatingContract, setTerminatingContract] = useState<ContractListItem | null>(null);
    const [terminating, setTerminating] = useState<boolean>(false);

    const handleTerminateClick = (contract: ContractListItem): void => {
        setTerminatingContract(contract);
        setTerminateDialogOpen(true);
    };

    const handleCloseTerminateDialog = (): void => {
        setTerminateDialogOpen(false);
        setTerminatingContract(null);
    };

    const handleConfirmTerminate = async (): Promise<void> => {
        if (!token || !terminatingContract) return;
        setTerminating(true);
        try {
            const result = await terminateContract(token, terminatingContract.employeeId, terminatingContract.contractId);
            if (result.success) {
                setSuccessMessage(t('contracts.terminate.success'));
                await refetchContracts();
            } else {
                setErrorMessage(result.error || t('contracts.terminate.error'));
            }
        } catch (err) {
            console.error('Error terminating contract:', err);
            setErrorMessage(t('common.error.unexpected'));
        } finally {
            setTerminating(false);
            setTerminateDialogOpen(false);
            setTerminatingContract(null);
        }
    };

    // ── PDF download ──
    const [downloadingContractId, setDownloadingContractId] = useState<string | null>(null);

    const handleDownloadDocument = async (contract: ContractListItem, format: 'pdf' | 'word'): Promise<void> => {
        if (!token || downloadingContractId) return;
        setDownloadingContractId(contract.contractId);
        try {
            const result = await downloadContractDocument(token, contract.contractId, format);
            if (!result.success || !result.blob) {
                setErrorMessage(t('contracts.download.error'));
                return;
            }
            const url = URL.createObjectURL(result.blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = result.fileName ?? 'kontrate';
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(url);
        } finally {
            setDownloadingContractId(null);
        }
    };

    const handleDownloadPdf = (contract: ContractListItem): Promise<void> => handleDownloadDocument(contract, 'pdf');
    const handleDownloadWord = (contract: ContractListItem): Promise<void> => handleDownloadDocument(contract, 'word');

    // ── Preview & sign ──
    const [previewContract, setPreviewContract] = useState<ContractListItem | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [previewDoc, setPreviewDoc] = useState<{documentId: string; hash: string; signed: boolean} | null>(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [signing, setSigning] = useState(false);

    const applyPreview = (result: ContractPreviewResult): void => {
        if (!result.blob || !result.documentId || !result.hash) return;
        setPreviewUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return URL.createObjectURL(result.blob as Blob);
        });
        setPreviewDoc({documentId: result.documentId, hash: result.hash, signed: !!result.signed});
    };

    const handlePreviewContract = async (contract: ContractListItem): Promise<void> => {
        if (!token) return;
        setPreviewContract(contract);
        setPreviewLoading(true);
        try {
            const result = await previewContractApi(token, contract.contractId);
            if (!result.success) {
                setErrorMessage(t('contracts.preview.error'));
                setPreviewContract(null);
                return;
            }
            applyPreview(result);
        } finally {
            setPreviewLoading(false);
        }
    };

    const handleClosePreview = (): void => {
        setPreviewUrl((old) => {
            if (old) URL.revokeObjectURL(old);
            return null;
        });
        setPreviewDoc(null);
        setPreviewContract(null);
    };

    const handleSignContract = async (): Promise<void> => {
        if (!token || !previewContract || !previewDoc || signing) return;
        setSigning(true);
        try {
            const result = await signContractApi(token, previewContract.contractId, previewDoc.documentId, previewDoc.hash);
            if (!result.success) {
                const key = result.status === 503 ? 'contracts.sign.notConfigured'
                    : result.status === 409 ? 'contracts.sign.conflict'
                        : 'contracts.sign.error';
                setErrorMessage(t(key));
                return;
            }
            applyPreview(result);
            setSuccessMessage(t('contracts.sign.success'));
        } finally {
            setSigning(false);
        }
    };

    const handleCloseSnackbar = (): void => {
        setSuccessMessage('');
        setErrorMessage('');
    };

    // ── Employees (for the Add Contract dialog's employee picker, and the upload flow) ──
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [employeesLoading, setEmployeesLoading] = useState<boolean>(false);

    const fetchEmployees = (): Promise<void> => {
        if (!token) return Promise.resolve();
        setEmployeesLoading(true);
        return getEmployees(token)
            .then((result) => {
                if (result.success) setEmployees(result.employees ?? []);
            })
            .finally(() => {
                setEmployeesLoading(false);
            });
    };

    useEffect(() => {
        fetchEmployees();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    // Called after a bulk contract upload completes successfully: refreshes
    // the visible contracts list and the employees list (hasActiveContract flags).
    const handleContractsUploaded = async (): Promise<void> => {
        await refetchContracts();
        await fetchEmployees();
        setSuccessMessage(t('contracts.upload.success'));
    };

    // ── Add Contract dialog ──
    const [addDialogOpen, setAddDialogOpen] = useState<boolean>(false);
    const [addBranchId, setAddBranchId] = useState<string>('');
    const [addEmployeeId, setAddEmployeeId] = useState<string>('');
    const [addForm, setAddForm] = useState<Contract>(EMPTY_CONTRACT);
    const [addSubmitError, setAddSubmitError] = useState<{ message: string; violations: string[] } | null>(null);
    const [addSubmitting, setAddSubmitting] = useState<boolean>(false);

    const employeesInAddBranch = useMemo(
        () => employees.filter((e) => e.branchId === addBranchId && !e.hasActiveContract),
        [employees, addBranchId],
    );

    // True when the selected branch has employees, but every one of them already has a
    // valid contract (so employeesInAddBranch is empty for a reason other than "no employees").
    const allAddBranchEmployeesHaveContract = useMemo(
        () => employees.some((e) => e.branchId === addBranchId) && employeesInAddBranch.length === 0,
        [employees, addBranchId, employeesInAddBranch],
    );

    const addBranchName = useMemo(
        () => branches.find((b) => b.id === addBranchId)?.name ?? '',
        [branches, addBranchId],
    );

    const handleOpenAddDialog = (): void => {
        setAddBranchId(selectedBranch && selectedBranch !== ALL_BRANCHES ? selectedBranch : '');
        setAddEmployeeId('');
        setAddForm(EMPTY_CONTRACT);
        setAddSubmitError(null);
        setAddDialogOpen(true);
    };

    const handleCloseAddDialog = (): void => {
        setAddDialogOpen(false);
        setAddBranchId('');
        setAddEmployeeId('');
        setAddForm(EMPTY_CONTRACT);
        setAddSubmitError(null);
    };

    const handleAddBranchChange = (event: SelectChangeEvent<string>): void => {
        setAddBranchId(event.target.value);
        setAddEmployeeId('');
    };

    const handleAddEmployeeChange = (event: SelectChangeEvent<string>): void => {
        setAddEmployeeId(event.target.value);
    };

    const handleAddFormChange = (field: keyof Contract) => (event: any): void => {
        setAddForm((prev) => ({...prev, [field]: event.target.value}));
    };

    const handleSubmitAdd = async (): Promise<void> => {
        if (!token) return;
        if (!addEmployeeId) {
            setAddSubmitError({message: t('contracts.add.selectEmployeeError'), violations: []});
            return;
        }
        if (!isContractFormValid(addForm)) {
            setAddSubmitError({message: t('contracts.edit.validationError'), violations: []});
            return;
        }
        setAddSubmitting(true);
        try {
            const result = await addContract(token, addEmployeeId, addForm);
            if (result.success) {
                handleCloseAddDialog();
                await refetchContracts();
                await fetchEmployees();
                setSuccessMessage(t('contracts.add.success'));
            } else {
                setAddSubmitError({
                    message: result.error || t('contracts.add.error'),
                    violations: result.violations || [],
                });
            }
        } catch (err) {
            console.error('Error adding contract:', err);
            setAddSubmitError({message: t('common.error.unexpected'), violations: []});
        } finally {
            setAddSubmitting(false);
        }
    };

    return {
        branches,
        branchesLoading,
        selectedBranch,
        handleBranchChange,
        typeFilter,
        handleTypeFilterChange,
        contractStats,
        searchInput,
        setSearchInput,
        contractsLoading,
        contractsError,
        filteredContracts,
        paginatedContracts,
        page,
        setPage,
        totalPages,
        rowsPerPage,
        viewDialogOpen,
        viewingContract,
        handleView,
        handleCloseView,
        editDialogOpen,
        editForm,
        editBranchName,
        editSubmitError,
        editSubmitting,
        handleEdit,
        handleCloseEditDialog,
        handleEditFormChange,
        handleSubmitEdit,
        terminateDialogOpen,
        terminatingContract,
        terminating,
        handleTerminateClick,
        handleCloseTerminateDialog,
        handleConfirmTerminate,
        handleDownloadPdf,
        handleDownloadWord,
        downloadingContractId,
        previewContract,
        previewUrl,
        previewDoc,
        previewLoading,
        signing,
        handlePreviewContract,
        handleClosePreview,
        handleSignContract,
        successMessage,
        errorMessage,
        handleCloseSnackbar,
        handleContractsUploaded,
        employees,
        employeesInAddBranch,
        allAddBranchEmployeesHaveContract,
        employeesLoading,
        addDialogOpen,
        addBranchId,
        addBranchName,
        addEmployeeId,
        addForm,
        addSubmitError,
        addSubmitting,
        handleOpenAddDialog,
        handleCloseAddDialog,
        handleAddBranchChange,
        handleAddEmployeeChange,
        handleAddFormChange,
        handleSubmitAdd,
    };
}

// ─── Contracts table action menu (three-dot menu, per row) ──────────────────

export function useContractsTableMenu() {
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedRowId, setSelectedRowId] = useState<string | null>(null);
    const open = Boolean(anchorEl);

    const handleMenuClick = (event: MouseEvent<HTMLElement>, contractId: string): void => {
        setAnchorEl(event.currentTarget);
        setSelectedRowId(contractId);
    };

    const handleMenuClose = (): void => {
        setAnchorEl(null);
        setSelectedRowId(null);
    };

    const handleMenuAction = (action: () => void): void => {
        action();
        handleMenuClose();
    };

    return {anchorEl, selectedRowId, open, handleMenuClick, handleMenuClose, handleMenuAction};
}
